import React, { useEffect, useState } from "react";
import { Empty, Table, Button, Modal, Typography, Row, Col, Image } from "antd";
import {
  LoadingOutlined,
  CalendarOutlined,
  TableOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { get_language, translate } from "../../locale";
import moment from "moment";
import { useParams } from "react-router-dom";
import UserServices from "../../services/UserServices";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import SecondaryButton from "../Buttons/SecondaryButton";
import LogBookServices from "../../services/LogBookServices";
import Link from "antd/lib/typography/Link";
import PropTypes from "prop-types";

const localizer = momentLocalizer(moment);

const { Title, Text } = Typography;

const NotepadCalendar = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [patient, setPatient] = useState(null);
  const [dataTable, setDataTable] = useState(null);
  const [modalDetails, setModalDetails] = useState(null);
  const [showAs, setShowAs] = useState("calendar");

  const { username } = useParams();
  const moods = {
    happy: "Contento",
    sad: "Triste",
    neutral: "Neutral",
    angry: "Enojado",
    irritated: "Irritado",
    distressed: "Angustiado",
    anxious: "Ansioso",
    indifferent: "Indiferente",
    excited: "Entusiasmado",
  };
  useEffect(() => {
    loadPatientInfo();
  }, []);

  const loadPatientInfo = async () => {
    try {
      const res = await UserServices.getProfile(username);
      if (res.status >= 200 && res.status < 300) {
        setPatient(res.data.my_contact_info);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [date]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await LogBookServices.getNotepad(username, date);
      if (
        response.status >= 200 &&
        response.status <= 300 &&
        response.data.notes.length !== 0
      ) {
        let newData = response.data.notes.map((i) => {
          const registerDate = i.register_date;
          const registerDatePlusOne = new Date(registerDate);
          registerDatePlusOne.setHours(registerDatePlusOne.getHours() + 1);

          return {
            ...i,
            start: new Date(i.register_date),
            end: registerDatePlusOne,
            type: "cell",
          };
        });
        setDataTable(response.data.notes);
        setData(newData);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const antIcon = (
    <LoadingOutlined
      style={{
        fontSize: 24,
      }}
      spin
    />
  );

  const columns = [
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex space-x-4 justify-center">
          <Button
            type="text"
            icon={<EyeOutlined />}
            size="small"
            className="border-0"
            onClick={() => handleDetails(record)}
          />
        </div>
      ),
    },
    {
      title: "Descripción",
      dataIndex: "text",
      key: "text",
      render: (_, record) => {
        if (record.text) {
          return record.text.length > 100
            ? `${record.text.substring(0, 100)}...`
            : record.text;
        }
        return "";
      },
      sorter: (a, b) => {
        if (a.text < b.text) return -1;
        if (a.text > b.text) return 1;
        return 0;
      },
    },
    {
      title: "Estado de ánimo",
      dataIndex: "mood",
      key: "mood",
      render: (_, record) => {
        return moods[record.mood] ?? "";
      },
      sorter: (a, b) => {
        if (a.mood < b.mood) return -1;
        if (a.mood > b.mood) return 1;
        return 0;
      },
    },
    {
      title: "Fecha",
      dataIndex: "intake_date",
      key: "intake_date",
      render: (_, record) => {
        return moment(record.register_date).format(
          get_language === "en" ? "MM/DD/YYYY" : "DD/MM/YYYY"
        );
      },
      sorter: (a, b) => {
        if (a.register_date < b.register_date) return -1;
        if (a.register_date > b.register_date) return 1;
        return 0;
      },
    },
    {
      title: "Hora",
      dataIndex: "intake_time",
      key: "intake_time",
      render: (_, record) => moment(record.register_date).format("HH:mm:ss"),
      sorter: (a, b) => {
        if (a.register_date < b.register_date) return -1;
        if (a.register_date > b.register_date) return 1;
        return 0;
      },
    },
  ];

  const handleHideDetails = () => {
    setModalDetails(null);
  };

  const handlePrev = () => {
    setDate((v) => moment(v).subtract(7, "days").format("YYYY-MM-DD"));
  };
  const handleToday = () => {
    setDate((v) => moment().format("YYYY-MM-DD"));
  };
  const handleNext = () => {
    if (date !== moment().format("YYYY-MM-DD")) {
      setDate((v) => moment(v).add(7, "days").format("YYYY-MM-DD"));
    }
  };
  const handleDetails = (item) => {
    setModalDetails(item);
  };
  return (
    <Row className="w-full">
      <Col flex="column" className="w-full" id="Notepad">
        <Row className="w-full">
          <Col span={24} className="mt-4">
            <Title level={2} className="text-center">
              {translate("Notas de ")}
              {patient && `${patient.user.full_name}`}
            </Title>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col span={24} className="flex flex-row justify-end space-x-2">
            <span className="flex flex-col justify-center">
              {translate("Formato")}:
            </span>
            <Button
              icon={<CalendarOutlined />}
              onClick={() => setShowAs("calendar")}
              type={showAs === "calendar" ? "primary" : "default"}
            />
            <Button
              icon={<TableOutlined />}
              onClick={() => setShowAs("table")}
              type={showAs === "table" ? "primary" : "default"}
            />
          </Col>
        </Row>
        {showAs === "calendar" && (
          <Calendar
            localizer={localizer}
            defaultDate={null}
            defaultView={Views.WEEK}
            views={["week"]}
            events={data || []}
            startAccessor="start"
            endAccessor="end"
            step={60}
            timeslots={6}
            style={{ height: 500 }}
            components={{
              toolbar: (props) => (
                <div className="flex flex-row my-4">
                  <div>
                    <SecondaryButton
                      className="mr-4"
                      onClick={() => {
                        props.onNavigate("PREV");
                        handlePrev();
                      }}
                    >
                      &lt;
                    </SecondaryButton>
                    <SecondaryButton
                      className="mr-4"
                      onClick={() => {
                        props.onNavigate("TODAY");
                        handleToday();
                      }}
                    >
                      Hoy
                    </SecondaryButton>
                    <SecondaryButton
                      className=""
                      onClick={() => {
                        props.onNavigate("NEXT");
                        handleNext();
                      }}
                    >
                      &gt;
                    </SecondaryButton>
                  </div>
                  <div className="flex flex-1 flex-row justify-center items-center">
                    {props.label}
                  </div>
                </div>
              ),
              week: {
                event: ({ event }) => {
                  return (
                    <Link
                      className={`text-center w-full block ${
                        event.type === "cell" ? "" : "rbc-event-content-header"
                      }`}
                      style={{ color: "white" }}
                      onClick={(e) => {
                        e.preventDefault();
                        if (event.type === "cell") {
                          handleDetails(event);
                        }
                      }}
                    >
                      {event.start.toString().indexOf("00:00:00") === -1
                        ? moment(event.start).format("HH:mm")
                        : event.title}
                    </Link>
                  );
                },
              },
            }}
            formats={{
              eventTimeRangeFormat: () => {
                return "";
              },
              timeGutterFormat: (date, culture, localizer) => {
                const time = localizer.format(date, "HH:mm", culture);
                let rs = "";
                switch (time) {
                  case "00:00":
                    rs = "";
                    break;
                  case "06:00":
                    rs = "";
                    break;
                  case "12:00":
                    rs = "";
                    break;
                  case "18:00":
                    rs = "";
                    break;
                }
                return rs;
              },
            }}
          />
        )}
        {showAs === "table" && (
          <>
            <div className="flex flex-row my-4">
              <div>
                <SecondaryButton
                  className="mr-4"
                  onClick={() => {
                    handlePrev();
                  }}
                >
                  &lt;
                </SecondaryButton>
                <SecondaryButton
                  className="mr-4"
                  onClick={() => {
                    handleToday();
                  }}
                >
                  Hoy
                </SecondaryButton>
                <SecondaryButton
                  className=""
                  onClick={() => {
                    handleNext();
                  }}
                >
                  &gt;
                </SecondaryButton>
              </div>
            </div>
            <Table
              rowKey={(i) => i.id}
              className="w-full mt-6"
              dataSource={dataTable}
              columns={columns}
              loading={
                loading
                  ? { indicator: antIcon, className: "text-custom_blue" }
                  : false
              }
              locale={{
                emptyText: <Empty description={translate("No hay datos")} />,
                sortTitle: translate("Ordenar"),
                triggerDesc: translate("Ordenar (descendente)"),
                triggerAsc: translate("Ordenar (ascendente)"),
                cancelSort: translate("Cancelar orden"),
                filterTitle: translate("Filtros"),
                filterConfirm: translate("Aceptar"),
                filterReset: translate("Limpiar"),
                filterEmptyText: translate("No hay filtros"),
                filterCheckall: translate("Seleccinar todos"),
                filterSearchPlaceholder: translate("Buscar en filtros"),
              }}
            />
          </>
        )}
        <Modal
          width={600}
          open={modalDetails !== null}
          title={translate("Detalles del block de notas")}
          onCancel={handleHideDetails}
          footer={[
            <Button key="ok" type="primary" onClick={handleHideDetails}>
              {translate("Aceptar")}
            </Button>,
          ]}
        >
          {modalDetails !== null && (
            <Row>
              <Col className="flex-1">
                <Row>
                  <Col>
                    <Text>
                      Fecha:{" "}
                      {moment(modalDetails.register_date).format("DD/MM/YYYY")}
                    </Text>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Text>
                      Hora: {moment(modalDetails.register_date).format("HH:mm")}
                    </Text>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Text>
                      Estado de ánimo:{" "}
                      {modalDetails.mood ? moods[modalDetails.mood] : ""}
                    </Text>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Text>{modalDetails.text}</Text>
                  </Col>
                </Row>
                {modalDetails.image_url && (
                  <Row>
                    <Col>
                      <Image
                        src={modalDetails.image_url}
                        style={{ maxHeight: window.innerHeight - 400 }}
                      />
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          )}
        </Modal>
      </Col>
    </Row>
  );
};

NotepadCalendar.propTypes = {
  onNavigate: PropTypes.func,
};

export default NotepadCalendar;
